import { useState } from "react";
import { mockRecentScans } from "@/mocks/statistics";

interface Scan {
  id: string;
  scannedAt: string;
  qrName: string;
  country: string;
  city: string;
  device: string;
  browser: string;
  ipHash: string;
}

interface Props {
  scans?: Scan[];
}

export default function RecentScansTable({ scans }: Props) {
  const [page, setPage] = useState(1);
  const perPage = 5;

  const data = scans && scans.length > 0
    ? scans.map((s) => {
        const date = new Date(s.scannedAt);
        return {
          id: s.id,
          date: date.toLocaleDateString("tr-TR", { day: "numeric", month: "short", year: "numeric" }),
          time: date.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" }),
          country: s.country,
          city: s.city,
          device: s.device,
          browser: s.browser,
          ip: s.ipHash,
        };
      })
    : mockRecentScans;

  const total = data.length;
  const pages = Math.ceil(total / perPage);
  const pageData = data.slice((page - 1) * perPage, page * perPage);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-50">
        <h3 className="font-bold text-gray-900">Son Taramalar</h3>
        <span className="text-xs text-gray-400">{total} kayıt</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50">
              {["QR Kod", "Tarih", "Ülke", "Şehir", "Cihaz", "Tarayıcı", "IP Hash"].map((col) => (
                <th key={col} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 whitespace-nowrap">
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageData.map((row) => (
              <tr key={row.id} className="border-t border-gray-50 hover:bg-gray-50/50 transition-colors">
                <td className="px-4 py-3 text-xs font-medium text-gray-700 whitespace-nowrap">{(row as { qrName?: string }).qrName || "—"}</td>
                <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{row.date} <span className="text-gray-400">{row.time}</span></td>
                <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{row.country}</td>
                <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{row.city}</td>
                <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{row.device}</td>
                <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{row.browser}</td>
                <td className="px-4 py-3 text-xs font-mono text-gray-400 whitespace-nowrap">{row.ip}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Pagination */}
      <div className="flex items-center justify-between px-6 py-4 border-t border-gray-50">
        <span className="text-xs text-gray-400">
          {(page - 1) * perPage + 1}–{Math.min(page * perPage, total)} / {total}
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-sm"></i>
          </button>
          {Array.from({ length: pages }).map((_, i) => (
            <button
              key={i}
              onClick={() => setPage(i + 1)}
              className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-semibold transition-colors cursor-pointer ${
                page === i + 1 ? "bg-emerald-500 text-white" : "border border-gray-200 text-gray-500 hover:bg-gray-50"
              }`}
            >
              {i + 1}
            </button>
          ))}
          <button
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
            disabled={page === pages}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-sm"></i>
          </button>
        </div>
      </div>
    </div>
  );
}